/**
 * Carries the caret (and any not-yet-committed text) of a paragraph across a
 * React unmount/remount, and between the fragments of one paragraph that is
 * split across pages.
 *
 * When the builder repaginates, a block can move from one page's BlockCanvas to
 * another, or a line can migrate from one fragment of a paragraph to the next.
 * The instance losing the caret stashes it here as a character offset into the
 * WHOLE paragraph; whichever instance of the same block covers that offset
 * takes it back.
 */

interface CaretRecord {
  blockId: string;
  html: string | null; // uncommitted DOM content (whole paragraphs only)
  offset: number; // caret position as a character offset into the whole paragraph's text
  createdAt: number;
}

let pending: CaretRecord | null = null;

const MAX_AGE_MS = 2000;

/** Character offset of the caret inside `root`, or null if the selection is elsewhere. */
export const getCaretOffset = (root: HTMLElement): number | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.endContainer)) return null;
  const preRange = document.createRange();
  preRange.selectNodeContents(root);
  preRange.setEnd(range.endContainer, range.endOffset);
  return preRange.toString().length;
};

/** Place the caret `offset` characters into `root`'s text. */
export const setCaretOffset = (root: HTMLElement, offset: number): void => {
  const selection = window.getSelection();
  if (!selection) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode();
  const range = document.createRange();
  while (node) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) {
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    node = walker.nextNode();
  }
  // Past the end (or no text nodes): collapse to the end of the element
  range.selectNodeContents(root);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

/**
 * Remember where the caret is. `baseOffset` is the character offset of this
 * element's text within the whole paragraph (0 unless it is a fragment).
 * `html` is only kept for whole paragraphs, whose DOM may hold uncommitted text.
 */
export const stashCaret = (blockId: string, root: HTMLElement, baseOffset = 0, keepHtml = true): void => {
  const local = getCaretOffset(root) ?? (root.textContent?.length ?? 0);
  pending = {
    blockId,
    html: keepHtml ? root.innerHTML : null,
    offset: baseOffset + local,
    createdAt: Date.now(),
  };
};

/** Stash an explicit whole-paragraph offset (e.g. to hand the caret to another fragment). */
export const stashCaretOffset = (blockId: string, offset: number): void => {
  pending = { blockId, html: null, offset, createdAt: Date.now() };
};

const isFresh = (record: CaretRecord) => Date.now() - record.createdAt <= MAX_AGE_MS;

/** Look at the pending record for `blockId` without consuming it. */
export const peekCaret = (blockId: string): { html: string | null; offset: number } | null => {
  if (!pending || pending.blockId !== blockId) return null;
  if (!isFresh(pending)) {
    pending = null;
    return null;
  }
  return { html: pending.html, offset: pending.offset };
};

/** Returns and clears the pending record if it belongs to `blockId` and is fresh. */
export const takeCaret = (blockId: string): { html: string | null; offset: number } | null => {
  const record = peekCaret(blockId);
  if (record) pending = null;
  return record;
};
